// ── Native push registration (Android/Capacitor) ─────────────────────────────
// Android counterpart to src/firebase/messaging.js's Tier 3 web push. Instead
// of a browser + VAPID key, the native FCM token comes straight from
// @capacitor/push-notifications and is stored in the same
// users/{uid}.fcmTokens map — api/cron/send-reminders.js already sends to
// every token in that map regardless of platform, so no backend changes are
// needed. No-ops entirely outside a native build (Capacitor.isNativePlatform()
// is false in the regular web app and Electron).
import { Capacitor, registerPlugin } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { doc, setDoc, updateDoc, FieldPath, deleteField } from 'firebase/firestore';
import { db } from '../firebase/config';
import { apiUrl } from '../firebase/apiUrl';

const AlarmScheduler = registerPlugin('AlarmScheduler');

export const isNativePush = Capacitor.isNativePlatform();

// Where the phone reports a snooze. A route on the alarm pusher rather than an
// endpoint of its own — see the top of api/cron/send-alarm-pushes.js.
const SNOOZE_REPORT_PATH = '/api/cron/send-alarm-pushes?action=snooze';

// Hands the native side what it needs to report a snooze to the server by
// itself (SnoozeUplink.java): Snooze is tapped on the alarm screen or its
// notification, usually with no WebView running to ask. The server checks
// the uid + token pair against users/{uid}.fcmTokens — the same pair saved
// below — and then pushes the snooze back when it's due, the way it pushes
// every other alarm. Called with {} on sign-out, which clears it. Best effort:
// an older native build without the method just rejects.
function shareIdentityWithNative(identity) {
  AlarmScheduler.setPushIdentity(identity).catch(() => {});
}

// The device's IANA zone ("Asia/Manila"). Guarded because resolvedOptions()
// can throw or report nothing on a locked-down WebView, and a missing
// timezone must never be the thing that stops a push token being saved.
function deviceTimeZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
  catch { return 'UTC'; }
}

let lastToken = null;
let listenersBound = false;
function bindListenersOnce(uid) {
  if (listenersBound) return;
  listenersBound = true;

  PushNotifications.addListener('registration', async (token) => {
    if (!token?.value) return;
    lastToken = token.value;
    shareIdentityWithNative({ uid, token: token.value, snoozeUrl: apiUrl(SNOOZE_REPORT_PATH) });
    await setDoc(doc(db, 'users', uid), {
      fcmTokens: { [token.value]: { updatedAt: Date.now(), ua: 'android-native' } },
      // Written alongside the token because the two are always needed
      // together: api/cron/send-alarm-pushes.js has to know what "07:30"
      // means for this person before it can decide an alarm is due now.
      // Without it the cron falls back to UTC and mis-times every alarm by
      // the user's whole offset — 8 hours in Manila. See lib/tz.js.
      timeZone: deviceTimeZone(),
    }, { merge: true });
  });

  PushNotifications.addListener('registrationError', (err) => {
    // eslint-disable-next-line no-console
    console.warn('[Clarity] native push registration failed', err);
  });
}

// Call once the user is signed in and has enabled notifications.
export async function registerNativePush(uid) {
  if (!isNativePush || !uid) return;
  const perm = await PushNotifications.checkPermissions();
  if (perm.receive !== 'granted') {
    const req = await PushNotifications.requestPermissions();
    if (req.receive !== 'granted') return;
  }
  bindListenersOnce(uid);
  await PushNotifications.register();
}

// Call on sign-out or when the user disables notifications.
export async function unregisterNativePush(uid) {
  if (!isNativePush) return;
  shareIdentityWithNative({});
  if (uid && lastToken) {
    await updateDoc(doc(db, 'users', uid), new FieldPath('fcmTokens', lastToken), deleteField()).catch(() => {});
  }
  try {
    await PushNotifications.removeAllDeliveredNotifications();
    await PushNotifications.removeAllListeners();
  } catch { /* best-effort */ }
  listenersBound = false;
  lastToken = null;
}
